// 112 §7 — THE LINKS MANAGER: linking after the fact (ADDENDUM, ruled
// 2026-08-21). "Once two items are created, I see no path to link them after
// the fact" — the chains were the only door. The server needed nothing:
// 111 §5a's routes accept any pair (create idempotent, delete by linkId,
// gates server-side). This dialog is the whole frontend.
//
// Opened from the footer's link line ("Link…", beside Unlink all).
//
// ── BRIEF 113 §4 — IT BECOMES TRANSACTIONAL, AND WHAT "DOESN'T WORK" WAS ────
//
// MEASURED FIRST, at the wire, reproducing the glance's exact flow (pick, pick,
// ×) on case 121. Both halves of the brief's dichotomy turned out to be true,
// of different clicks:
//
//   (a) A PICK CAN SILENTLY DO NOTHING. The dialog held two short fields, so it
//       collapsed to 185px tall over a 140px body. The Combobox menu is a
//       `position:absolute` sibling INSIDE that body, and `.modal-body` is
//       `overflow-y: auto` — a clip box. Combobox measures the room and caps
//       the menu to it, but the cap has a FLOOR: `Math.max(MIN_MENU_HEIGHT,
//       Math.min(MENU_MAX_HEIGHT, space))` (Combobox.tsx:204, floor 120px). In
//       a 140px body the room is ~85px, the floor wins, and the menu overflows
//       its own clip box by ~34px — measured. Those top rows are painted away
//       and a pointer aimed at them reaches the dialog HEADER: no toast, no
//       error, no write. Hit-tested with `elementFromPoint`, row by row.
//   (b) AND A PICK THAT LANDS SAYS NOTHING WHERE IT CAN BE SEEN. The write
//       itself was sound — a reachable row POSTed and came back 201 — but the
//       open menu covers the whole dialog, CURRENT LINKS included, so the one
//       surface that would confirm the write is behind the thing you clicked.
//
// So the size was not cosmetic: it was the defect. Standard proportions here
// mean a real list area (`minHeight` on CURRENT LINKS) that keeps ≥ the menu's
// own floor of room above the ADD LINK field, so the dropdown renders WHOLE
// inside the dialog — asserted in the drive, not assumed.
//
// BRIEF 114 §2 — (a) ABOVE IS HISTORY, NOT THE PRIMITIVE'S BEHAVIOUR ANY MORE.
// 113 cured this dialog by giving it room and left the flaw live at every other
// mount; 114 §2 fixed it where it lives. `MIN_MENU_HEIGHT` is now
// `PREFERRED_MIN_HEIGHT` and decides a FLIP, never a height, so a Combobox in a
// clip box shorter than its preference gets a shorter menu with internal
// scroll instead of an unclickable overflow. This dialog's 150px list floor
// STAYS: it is what keeps the dropdown whole and above the field rather than
// squeezed and scrolling, which is a proportion question, not a bug fix.
// (`maxWidth` was
// inert all along: `.modal` carries `max-width: 92vw !important`, which an
// inline style cannot outrank, so the dialog was always the house 480 — the
// same width as the task modal beside it. It is dropped rather than left
// looking load-bearing.)
//
// NOTHING WRITES UNTIL "Link & EXIT" (RULED). A pick STAGES an add; the × on a
// current link STAGES its removal (struck through, re-click restores); picks
// accumulate. Close / X / Escape discard every staged change — the wire stays
// silent. The primary commits the batch through the host and closes.
//
// THE TRADE, ENUMERATED: the per-removal Undo toast dies with the
// immediate-delete model. The staging IS the second chance — a removal is
// visible, reversible and free until the batch commits — and an Undo toast for
// a batch would have to unwind adds and removals together, which is a
// transaction the store does not offer.
//
// DUMB, STILL: the host (App) resolves labels the way the pills do, builds the
// candidate list (same matter only, self and already-linked excluded, archived
// never listed — §6's rule) and owns every write. `onCommit` returns the rows
// that FAILED; they stay staged and marked, and the dialog stays open.
import { useState } from "react";
import { useTranslation } from "../../i18n";
import { FS, Z } from "../../lib/tokens";
import { ModalLayout } from "../SharedUI";
import Combobox from "../Combobox";
import type { ItemLink } from "../../api";

/** What a commit could not do. Empty on both counts = everything landed. */
export type LinkCommitFailures = { adds: string[]; removes: number[] };

export default function LinksManagerDialog({
  links,
  labelOfOther,
  options,
  onCommit,
  onClose,
}: {
  /** The open item's own links (live — re-renders as the store moves). */
  links: ItemLink[];
  /** Kind-tagged label of the link's OTHER endpoint, resolved by the host. */
  labelOfOther: (link: ItemLink) => string;
  /** Candidates for the ADD box: kind-prefixed, exclusions already applied. */
  options: { value: string; label: string }[];
  /** POST every staged add, DELETE every staged removal; report what failed. */
  onCommit: (
    adds: string[],
    removes: ItemLink[],
  ) => Promise<LinkCommitFailures>;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [staged, setStaged] = useState<{ key: string; label: string }[]>([]);
  const [dropped, setDropped] = useState<number[]>([]);
  const [failed, setFailed] = useState<LinkCommitFailures>({
    adds: [],
    removes: [],
  });
  const [busy, setBusy] = useState(false);

  const stagedKeys = new Set(staged.map((s) => s.key));
  const free = options.filter((o) => !stagedKeys.has(o.value));

  const row = (
    key: string,
    label: string,
    struck: boolean,
    isNew: boolean,
    isFailed: boolean,
    onToggle: () => void,
    tip: string,
  ) => (
    <div
      key={key}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 6,
        padding: "3px 0",
        fontSize: FS.s11,
        color: isFailed ? "var(--rd)" : "var(--ts)",
      }}
      title={isFailed ? t("links.row_failed", { fallback: "Not saved — try again" }) : undefined}
    >
      {/* Language-free staging marks: a purple + for an add that has not been
          written yet, a strike-through for a removal that has not either. */}
      {isNew && (
        <span style={{ color: "var(--pp)", flexShrink: 0, fontWeight: 600 }}>+</span>
      )}
      <span
        style={{
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          textDecoration: struck ? "line-through" : undefined,
          opacity: struck ? 0.55 : 1,
        }}
      >
        {label}
      </span>
      {isFailed && (
        <span style={{ flexShrink: 0, fontSize: FS.s9_5 }}>
          {t("links.row_failed", { fallback: "Not saved — try again" })}
        </span>
      )}
      <button
        type="button"
        className="btn"
        style={{
          marginLeft: "auto",
          background: "none",
          border: "none",
          color: struck ? "var(--pp)" : "var(--tm)",
          cursor: busy ? "default" : "pointer",
          padding: "0 4px",
          fontSize: FS.s11,
          flexShrink: 0,
          lineHeight: 1,
        }}
        disabled={busy}
        title={tip}
        onClick={onToggle}
      >
        {struck ? "↩" : "×"}
      </button>
    </div>
  );

  return (
    <ModalLayout
      title={t("links.manager_title", { fallback: "Links" })}
      onClose={onClose}
      closeDisabled={busy}
      zIndex={Z.modal2}
      escToClose
      footer={
        /* The timelog convention, exactly: the quiet centred text link above
           the full-width primary. (§3's chain footer makes its close a BUTTON
           because it is one of three equals in a row; here there is no row, so
           the convention's own shape stands.) */
        <div className="modal-footer">
          <button
            type="button"
            className="modal-cancel"
            disabled={busy}
            onClick={onClose}
          >
            {t("links.close_without", { fallback: "Close without linking" })}
          </button>
          <button
            type="button"
            className="modal-save"
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              const removes = links.filter((l) => dropped.includes(l.id));
              const res = await onCommit(
                staged.map((s) => s.key),
                removes,
              );
              const stillFailing = {
                adds: res?.adds || [],
                removes: res?.removes || [],
              };
              setBusy(false);
              if (!stillFailing.adds.length && !stillFailing.removes.length) {
                onClose();
                return;
              }
              /* Errors surface PER ROW and the dialog stays open with the
                 failures still staged — what landed is gone from the staging
                 because the host's answer names only what did not. */
              setFailed(stillFailing);
              setStaged((prev) => prev.filter((s) => stillFailing.adds.includes(s.key)));
              setDropped((prev) => prev.filter((id) => stillFailing.removes.includes(id)));
            }}
          >
            {t("links.link_exit", { fallback: "Link & EXIT" })}
          </button>
        </div>
      }
    >
      <div className="field">
        <label>{t("links.manager_current", { fallback: "Current links" })}</label>
        {/* 113 §4 — THE LIST HAS A FLOOR, and it is what keeps the ADD LINK
            dropdown inside the dialog: the menu's own minimum is 120px plus a
            4px gap and an 8px margin, and it opens UPWARD from a field at the
            bottom of the body. Room above the field is exactly this box. */}
        <div style={{ minHeight: 150 }}>
          {links.length === 0 && staged.length === 0 ? (
            <div style={{ fontSize: FS.s10, color: "var(--tm)", fontStyle: "italic" }}>
              {t("links.manager_none", { fallback: "No links yet." })}
            </div>
          ) : (
            <>
              {links.map((l) =>
                row(
                  `l${l.id}`,
                  labelOfOther(l),
                  dropped.includes(l.id),
                  false,
                  failed.removes.includes(l.id),
                  () =>
                    setDropped((prev) =>
                      prev.includes(l.id) ? prev.filter((x) => x !== l.id) : [...prev, l.id],
                    ),
                  dropped.includes(l.id)
                    ? t("common.undo", { fallback: "Undo" })
                    : t("links.manager_remove_tip", { fallback: "Remove this link" }),
                ),
              )}
              {staged.map((s) =>
                row(
                  `s${s.key}`,
                  s.label,
                  false,
                  true,
                  failed.adds.includes(s.key),
                  () => setStaged((prev) => prev.filter((x) => x.key !== s.key)),
                  t("links.manager_remove_tip", { fallback: "Remove this link" }),
                ),
              )}
            </>
          )}
        </div>
      </div>
      <div className="field">
        <label>{t("links.manager_add", { fallback: "Add link" })}</label>
        <Combobox
          value=""
          options={free}
          disabled={busy}
          placeholder={t("links.manager_add_ph", { fallback: "Link to…" })}
          onChange={(v) => {
            const key = String(v || "");
            if (!key) return;
            const opt = options.find((o) => o.value === key);
            setStaged((prev) =>
              prev.some((s) => s.key === key)
                ? prev
                : [...prev, { key, label: opt?.label || key }],
            );
          }}
        />
      </div>
    </ModalLayout>
  );
}
